import React, { useState, useEffect } from 'react'
import { Row, Col, List, Card, Typography, Space, Button, Empty, message } from 'antd'
import {
  TransactionOutlined,
  HistoryOutlined,
  UserOutlined,
  PhoneOutlined,
  FileTextOutlined,
  CloseOutlined,
  DollarOutlined,
  SyncOutlined
} from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ComprehensiveBalance from '../../AccountBalance/ComprehensiveBalance'
import AccountBalanceSummary from '../../AccountBalance/AccountBalanceSummary'
import PaymentAllocationWizard from '../../PaymentAllocation/PaymentAllocationWizard'
import FollowUpTaskManager from '../../FollowUpTasks/FollowUpTaskManager'
import TransactionsContent from './components/TransactionsContent'
import ActivityActionsContent from './components/ActivityActionsContent'
import AccountDetailsContent from './components/AccountDetailsContent'
import ContactDetailsContent from './components/ContactDetailsContent'
import HistoryContent from './components/HistoryContent'
import NotesContent from './components/NotesContent'
import { faFileInvoiceDollar, faClipboardList, faStickyNote, faTasks } from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useDispatch } from 'react-redux'
import { syncMedicalAidPayments } from '../../../backend-integration/api/billingIntegration'

dayjs.extend(relativeTime)

const { Title, Text } = Typography

const AccountDetails = ({ accountData, onClose }) => {
  const [activeSection, setActiveSection] = useState('summary')
  const [syncingPayments, setSyncingPayments] = useState(false)
  const [showPaymentAllocation, setShowPaymentAllocation] = useState(false)

  const navigationItems = [
    {
      key: 'summary',
      icon: <DollarOutlined />,
      label: 'Account Summary'
    },
    {
      key: 'transactions',
      icon: <TransactionOutlined />,
      label: 'Transactions'
    },
    {
      key: 'activity-actions',
      icon: <HistoryOutlined />,
      label: (
        <span>
          Activity & Actions{' '}
          <span style={{ color: '#fa8c16', fontSize: '11px', fontWeight: 'normal' }}>Coming Soon</span>
        </span>
      )
    },
    {
      key: 'follow-up-tasks',
      icon: <FontAwesomeIcon icon={faTasks} />,
      label: (
        <span>
          Follow-up Tasks <span style={{ color: '#fa8c16', fontSize: '11px', fontWeight: 'normal' }}>Coming Soon</span>
        </span>
      )
    },
    {
      key: 'account-details',
      icon: <FileTextOutlined />,
      label: 'Account details'
    },
    {
      key: 'contact-details',
      icon: <PhoneOutlined />,
      label: 'Contact details'
    },
    {
      key: 'history',
      icon: <FontAwesomeIcon icon={faClipboardList} />,
      label: 'History'
    },
    {
      key: 'notes',
      icon: <FontAwesomeIcon icon={faStickyNote} />,
      label: 'Notes'
    }
  ]

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const renderContent = () => {
    switch (activeSection) {
      case 'summary':
        return <AccountBalanceSummary patientId={accountData.patientId} accountData={accountData} />
      case 'transactions':
        return <TransactionsContent accountData={accountData} formatCurrency={formatCurrency} />
      case 'activity-actions':
        return <ActivityActionsContent accountData={accountData} />
      case 'follow-up-tasks':
        return <FollowUpTaskManager accountData={accountData} patientId={accountData.patientId} />
      case 'account-details':
        return <AccountDetailsContent accountData={accountData} formatCurrency={formatCurrency} />
      case 'contact-details':
        return <ContactDetailsContent accountData={accountData} />
      case 'history':
        return <HistoryContent accountData={accountData} />
      case 'notes':
        return <NotesContent accountData={accountData} />
      default:
        return <Empty description='Select a section to view details' />
    }
  }

  const handleSyncMedicalAidPayments = async () => {
    setSyncingPayments(true)
    try {
      const result = await syncMedicalAidPayments(accountData.patientId)

      // Build detailed message based on results
      let messageType = 'info'
      let detailedMessage = ''

      if (result.syncedPayments > 0) {
        messageType = 'success'
        detailedMessage = `✅ ${result.syncedPayments} medical aid payment(s) recorded\n`
      }

      if (result.results && result.results.length > 0) {
        // Group results by action type
        const approvals = result.results.filter((r) => r.action === 'approval_recorded')
        const payments = result.results.filter((r) => r.action === 'payment_created')
        const pending = result.results.filter((r) => r.status === 'approved_no_amount' || r.status === 'pending')
        const rejected = result.results.filter((r) => r.status === 'rejected')

        if (approvals.length > 0) {
          detailedMessage += `📋 ${approvals.length} claim(s) marked as approved\n`
        }

        if (pending.length > 0) {
          detailedMessage += `⏳ ${pending.length} claim(s) approved but awaiting payment\n`
        }

        if (rejected.length > 0) {
          messageType = 'warning'
          detailedMessage += `❌ ${rejected.length} claim(s) rejected by medical aid\n`
        }
      }

      if (result.alreadyPaid > 0) {
        detailedMessage += `✓ ${result.alreadyPaid} invoice(s) already have payments`
      }

      if (!detailedMessage) {
        detailedMessage = 'No new medical aid updates found'
      }

      // Show appropriate message
      if (messageType === 'success') {
        message.success(detailedMessage.trim())
      } else if (messageType === 'warning') {
        message.warning(detailedMessage.trim())
      } else {
        message.info(detailedMessage.trim())
      }

      // Refresh the current view to show new transactions
      if (
        result.syncedPayments > 0 ||
        (result.results && result.results.some((r) => r.action === 'approval_recorded'))
      ) {
        if (activeSection === 'transactions') {
          // This will trigger a refresh in TransactionsContent
          setActiveSection('refresh')
          setTimeout(() => setActiveSection('transactions'), 0)
        }
      }

      console.log('Sync results:', result)
    } catch (error) {
      message.error('Failed to sync medical aid payments: ' + error.message)
      console.error('Sync error:', error)
    } finally {
      setSyncingPayments(false)
    }
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div
        style={{
          padding: '16px 24px',
          borderBottom: '1px solid #f0f0f0',
          background: '#fafafa'
        }}
      >
        <Row justify='space-between' align='middle'>
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              {accountData.patientName} - Account Details
            </Title>
            <Text type='secondary'>Billing ID: {accountData.billingId}</Text>
          </Col>
          <Col>
            <Space>
              <Button icon={<DollarOutlined />} onClick={() => setShowPaymentAllocation(true)} type='primary'>
                Record Payment
              </Button>
              {accountData.medicalAid && (
                <Button
                  icon={<SyncOutlined spin={syncingPayments} />}
                  onClick={handleSyncMedicalAidPayments}
                  type='primary'
                  ghost
                  loading={syncingPayments}
                >
                  Sync Medical Aid Payments
                </Button>
              )}
              <Button icon={<CloseOutlined />} onClick={onClose} type='text' />
            </Space>
          </Col>
        </Row>
      </div>

      {/* Content Area */}
      <Row style={{ flex: 1, overflow: 'hidden' }}>
        {/* Navigation Sidebar */}
        <Col
          span={5}
          style={{
            borderRight: '1px solid #f0f0f0',
            background: '#fafafa',
            height: '100%',
            overflow: 'auto'
          }}
        >
          <List
            dataSource={navigationItems}
            renderItem={(item) => (
              <List.Item
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  background: activeSection === item.key ? '#fff' : 'transparent',
                  borderLeft: activeSection === item.key ? '3px solid #1890ff' : '3px solid transparent',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => setActiveSection(item.key)}
              >
                <Space>
                  <span
                    style={{
                      fontSize: '16px',
                      color: activeSection === item.key ? '#1890ff' : '#595959'
                    }}
                  >
                    {item.icon}
                  </span>
                  <Text strong={activeSection === item.key}>{item.label}</Text>
                </Space>
              </List.Item>
            )}
          />
        </Col>

        {/* Content Area */}
        <Col
          span={19}
          style={{
            padding: '24px',
            height: '100%',
            overflow: 'auto',
            background: '#fff'
          }}
        >
          {renderContent()}
        </Col>
      </Row>

      {/* Payment Allocation Wizard */}
      <PaymentAllocationWizard
        visible={showPaymentAllocation}
        onClose={(success) => {
          setShowPaymentAllocation(false)
          if (success && activeSection === 'transactions') {
            // Refresh transactions
            setActiveSection('refresh')
            setTimeout(() => setActiveSection('transactions'), 0)
          }
        }}
        patientId={accountData.patientId}
        accountData={accountData}
      />
    </div>
  )
}

export default AccountDetails
